import { initializeApp, getApp } from "firebase/app"
import { Capacitor } from '@capacitor/core'
import { connectFirestoreEmulator, getFirestore, initializeFirestore, setLogLevel } from "firebase/firestore"
import { Auth, getAuth, initializeAuth, indexedDBLocalPersistence, signInAnonymously, User as FirebaseAuthUser } from "firebase/auth"
import { getFunctions, httpsCallable } from "firebase/functions"
import { FirebaseAnalytics } from '@capacitor-firebase/analytics'
import { connectFunctionsEmulator } from "firebase/functions"
import { ErrorReportRequest } from './ErrorFallback'
import { config } from './firebaseCredentials'

//
// For testing Firebase Functions V2 via the local Functions emulator, i.e.:
//   cd functions-v2
//   npm run serve
//
// For testing Firebase Firestore Rules via the local Firestore emulator...
//
const USE_FIREBASE_FUNCTIONS_EMULATOR = false
const USE_FIREBASE_FIRESTORE_EMULATOR = false

const databaseLogging = 'error' // 'debug'

type CloudFunctionResponse = {
    ok?: boolean
    code?: string
    error?: string
    uid?: string
    clubId?: string
}

// per github suggestion
//
// https://github.com/firebase/firebase-js-sdk/issues/5019
//
let auth: Auth | null = null
let authUserPromise: Promise<FirebaseAuthUser> | null = null

export function getFirebaseAuth() {
    // console.log('getFirebaseAuth()!', auth)
    if (!auth) {
        if (Capacitor.isNativePlatform()) {
            auth = initializeAuth(getApp(), {
                persistence: indexedDBLocalPersistence,
            })
        } else {
            auth = getAuth()
        }
        // auth.onAuthStateChanged(newUser => console.log('new user', newUser))
    }
    // console.log('auth is now', auth)
	return auth
}

export async function ensureFirebaseAuthUser(): Promise<FirebaseAuthUser> {
	const firebaseAuth = getFirebaseAuth()
	if (firebaseAuth.currentUser) {
		return firebaseAuth.currentUser
	}

	if (!authUserPromise) {
		authUserPromise = signInAnonymously(firebaseAuth)
			.then(result => result.user)
			.finally(() => {
				authUserPromise = null
			})
	}

	return authUserPromise
}

const firebaseApp = initializeApp(config)

//
// Firestore iOS/Safari transport note
//
// In June 2026 we diagnosed a native iOS/Capacitor-only delay where Firestore
// listeners sometimes delivered an initial burst of data, paused for ~30s, then
// delivered the rest together. Safari Web Inspector showed long-running
// firestore.googleapis.com Listen/channel requests, and app diagnostics showed
// the delay was in Firestore listener delivery rather than React rendering.
//
// Relevant reports:
// - https://github.com/firebase/firebase-js-sdk/issues/9789
// - https://www.reddit.com/r/Firebase/comments/1s7lpzz/degraded_firestore_performance_only_on_safari_264/
// - https://firebase.google.com/docs/reference/js/firestore_.firestoresettings
//
// What we tried:
// - experimentalAutoDetectLongPolling did not visibly help.
// - The durable fix was to reduce Club's listener fan-out in data.ts. Club used
//   to open one live userTeams query per club user; it now batches team loading
//   with managers array-contains-any chunks, so a 30+ coach club opens a few
//   team listeners instead of dozens.
// - On 24 June 2026 the delay returned during ordinary iOS team switching, so
//   we enabled experimentalForceLongPolling for native Capacitor builds only.
//   Web builds still use the default transport.
//
// If this still returns, first check for new high-fan-out Firestore listener
// patterns. If the query pattern is reasonable, monitor the Firebase issue and
// retest the default transport when Safari/WebKit or the Firebase JS SDK ships
// a relevant fix.
//
export const db = Capacitor.isNativePlatform()
	? initializeFirestore(firebaseApp, {
		experimentalForceLongPolling: true,
	})
	: getFirestore(firebaseApp)
setLogLevel(databaseLogging)

if (USE_FIREBASE_FIRESTORE_EMULATOR && import.meta.env.DEV) {
	connectFirestoreEmulator(db, 'localhost', 8080)
	console.error("***** USING CLOUD FIRESTORE EMULATOR *****")
	setTimeout(() => {
		console.log('*********************************************')
		console.error("***** USING CLOUD FIRESTORE EMULATOR *****")
		console.log('*********************************************')
	}, 1500)
}


//
// Analytics
//

FirebaseAnalytics.setEnabled({
    enabled: true,
})

export const setAnalyticsUserId = async (userId: UserId) => {
    await FirebaseAnalytics.setUserId({
        userId,
    })
}

/*
export const setAnalyticsUserProperty = async (key: string, value: any) => {
    await FirebaseAnalytics.setUserProperty({
        key,
        value,
    })
}
*/

export const logEvent = async (name: string, params?: any) => {
    console.log('logEvent', name, params)
    await FirebaseAnalytics.logEvent({
		name,
		params,
	})
}

//
// Cloud Functions
//

const functions = getFunctions(firebaseApp, 'australia-southeast1')

if (USE_FIREBASE_FUNCTIONS_EMULATOR && import.meta.env.DEV) {
	connectFunctionsEmulator(functions, "localhost", 5001)
	console.error("***** USING CLOUD FUNCTIONS EMULATOR *****")
	setTimeout(() => {
		console.log('*********************************************')
		console.error("***** USING CLOUD FUNCTIONS EMULATOR *****")
		console.log('*********************************************')
	}, 1500)
}

//
// To test /functions-v2/index.ts -> stripeV2(), which isn't listed below, you need
// to simulate a call from Stripe.
//
// Edit firebase.ts -> USE_FIREBASE_FUNCTIONS_EMULATOR = true
//
//   cd functions-v2
//   npm run serve
//   stripe login
//   stripe listen --forward-to localhost:5001/netball-autoroster/australia-southeast1/stripeV2
//   stripe trigger checkout.session.completed
//
// However to actually do a whole payment test, you need to edit WebPurchasing.tsx -> publishable-key
// to use the test key available here:
//   https://dashboard.stripe.com/test/apikeys
// ...then monitor here:
//  https://dashboard.stripe.com/test/payments
//
export const clubUserLogin = httpsCallable<ClubUserLoginRequest, CloudFunctionResponse>(functions, 'clubUserLoginV2') 				// ok
export const joinClub = httpsCallable<JoinClubRequest, CloudFunctionResponse>(functions, 'joinClubV2') 								// ok
export const leaveClub = httpsCallable<LeaveClubRequest, CloudFunctionResponse>(functions, 'leaveClubV2') 							// ok
export const ejectUserFromClub = httpsCallable<EjectUserRequest, CloudFunctionResponse>(functions, 'ejectUserFromClubV2') 			// ok
export const setClubUserStatus = httpsCallable<SetClubUserStatusRequest, CloudFunctionResponse>(functions, 'setClubUserStatusV2') 	// ok
export const modifyCoachAccess = httpsCallable<ModifyCoachAccessRequest, CloudFunctionResponse>(functions, 'modifyCoachAccessV2') 	// ok
export const errorReport = httpsCallable<ErrorReportRequest, CloudFunctionResponse>(functions, 'errorReportV2') 					// ok
export const checkTeamFlowEntitlement = httpsCallable<CheckTeamFlowEntitlementRequest, CheckTeamFlowEntitlementResponse>(
	functions,
	'checkTeamFlowEntitlementV2',
)
